// Pre-visit brief, AI Chat side. The brief is written server-side (backend/api/previsit_brief.py)
// about an hour before the appointment, or whenever the chart is opened without one, before the
// appointment or after it (useVisitBrief asks on open), and lives in the chat as a real, pinned exchange
// (backend/api/previsit_brief_chat.py), so this component has two jobs:
//   * while there is no READY brief, a slim strip at the top of the chat says why (and offers
//     Generate now / Retry);
//   * it asks the server to file the brief in the chat, hands the answer to `onAttached` so the
//     chat can show the exchange without reloading, and once one is ready renders nothing: the
//     brief is in the conversation and in the chat's pinned bar.
//
// The chat thread is keyed by the VISIT, and the front desk can re-point a visit at another
// patient, so a brief filed in the chat for the old patient would stay pinned, visible and
// replayed to the model. The server therefore takes such a brief out of the chat (and says which
// in the answer) on EVERY call, brief or no brief. So the chat is asked on every open, as soon
// as the brief lookup has answered, whatever state the brief is in; and again each time a
// brief lands (a new `generated_at`) or the doctor presses Retry. A given ask goes out at most
// once per (visit, chat, mount), StrictMode included.
import React, { useEffect, useRef, useState } from "react";
import useVisitBrief from "./useVisitBrief";
import { isBriefEligible } from "./visitBriefTiming";
import { attachVisitBriefToChat } from "../../../services/visitBriefService";

const ATTACH_FAILED = "Couldn't add the brief to the AI chat.";

function Strip({ busy = false, children }) {
  return (
    <section
      aria-label="Pre-visit brief"
      aria-busy={busy ? "true" : undefined}
      aria-live="polite"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-purple-200 dark:border-purple-500/30 bg-purple-50/70 dark:bg-purple-500/[0.06] px-3 py-1.5"
    >
      <span className="shrink-0 text-sm leading-none" aria-hidden="true">📋</span>
      {children}
    </section>
  );
}

// A div, not a p: the clinic stylesheet has no preflight, so a p would bring the browser's
// own margins into a strip that is meant to be slim. `basis-40` lets two messages and a button
// wrap onto a second line in a narrow panel instead of squeezing each other.
function Message({ error = false, children }) {
  return (
    <div
      className={`min-w-0 flex-1 basis-40 text-[11px] leading-snug ${
        error ? "text-rose-600 dark:text-rose-400" : "text-slate-600 dark:text-slate-300"
      }`}
    >
      {children}
    </div>
  );
}

function ActionButton({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="shrink-0 px-2 py-0.5 text-[11px] font-semibold rounded bg-purple-600 hover:bg-purple-500 text-white shadow-sm"
    >
      {children}
    </button>
  );
}

// Everything the strip does for ONE visit. Only rendered for an eligible chart, and remounted
// per visit (and per chat) by VisitBriefChatStrip's key.
function StripPanel({ schema, patient, chatPatientId, onAttached }) {
  const { loading, data, pending, requestError, regenerate } = useVisitBrief({
    schema,
    visitId: patient.id,
  });
  const status = pending ? "generating" : data?.status || (loading ? "loading" : "none");
  const generatedAt = status === "ready" && data?.generated_at ? String(data.generated_at) : "";

  // Asking the server to file the brief in the chat (and to clear another patient's): once when
  // the lookup has answered, whatever it says ("open"), once per brief that is ready (its
  // generated_at), and once more for each Retry after a failure. The ask is remembered by its key
  // in `sentRef`, which survives StrictMode's second run of the effect; nothing else that can
  // change while this panel is mounted takes part (the key pins schema, visit and chat), so a
  // re-render, a refetch of the patient or a change in the brief's state does not send it again.
  const [attachError, setAttachError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const aliveRef = useRef(true);
  const sentRef = useRef(new Set());
  const onAttachedRef = useRef(onAttached);

  useEffect(() => {
    onAttachedRef.current = onAttached;
  });

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const attachKey = loading ? "" : `${attempt}::${generatedAt || "open"}`;

  useEffect(() => {
    if (!attachKey || sentRef.current.has(attachKey)) return;
    sentRef.current.add(attachKey);
    setAttachError(null);
    (async () => {
      try {
        const result = await attachVisitBriefToChat({
          schema,
          visitId: patient.id,
          chatPatientId,
        });
        // A visit that is no longer on screen has no chat for this answer to join.
        if (aliveRef.current) onAttachedRef.current?.(result);
      } catch (err) {
        // Kept either way; shown only once a brief is ready (below): with nothing to file, a
        // strip saying the brief could not be added to the chat would be talking about nothing.
        if (aliveRef.current) setAttachError(err?.message || ATTACH_FAILED);
      }
    })();
  }, [attachKey, schema, patient.id, chatPatientId]);

  const retryAttach = () => {
    setAttachError(null);
    setAttempt((n) => n + 1);
  };

  if (status === "ready") {
    if (!attachError) return null; // the brief is in the chat now
    return (
      <Strip>
        <Message error>{attachError}</Message>
        <ActionButton onClick={retryAttach}>Retry</ActionButton>
      </Strip>
    );
  }
  if (status === "loading") {
    return (
      <Strip busy>
        <Message>Loading pre-visit brief…</Message>
      </Strip>
    );
  }
  if (status === "none") {
    if (requestError) {
      return (
        <Strip>
          <Message error>{requestError}</Message>
          <ActionButton onClick={regenerate}>Retry</ActionButton>
        </Strip>
      );
    }
    // Nothing in flight: the request made on open shows up as "generating" below, which is
    // the only place the progress text belongs. Landing here means nothing is driving a brief
    // (a poll answered "none" after a request), so offer the request instead of a spinner.
    return (
      <Strip>
        <Message>No brief yet for this visit.</Message>
        <ActionButton onClick={regenerate}>Generate now</ActionButton>
      </Strip>
    );
  }
  if (status === "generating") {
    // A generating strip always has a way out: when polling failed or gave up, the reason and
    // Retry sit beside the progress line instead of an endless spinner.
    return (
      <Strip busy={!requestError}>
        <Message>
          <span className="inline-block animate-spin" aria-hidden="true">🌀</span> Preparing brief…
        </Message>
        {requestError ? (
          <>
            <Message error>{requestError}</Message>
            <ActionButton onClick={regenerate}>Retry</ActionButton>
          </>
        ) : null}
      </Strip>
    );
  }
  if (status === "empty") {
    return (
      <Strip>
        <Message>No past visits, reports or forms on file for this patient.</Message>
        {requestError ? <Message error>{requestError}</Message> : null}
        <ActionButton onClick={regenerate}>Regenerate</ActionButton>
      </Strip>
    );
  }
  if (status === "failed") {
    return (
      <Strip>
        <Message error>Couldn't generate the brief.</Message>
        {requestError ? <Message error>{requestError}</Message> : null}
        <ActionButton onClick={regenerate}>Retry</ActionButton>
      </Strip>
    );
  }
  return null;
}

// The keyed remount gives every visit (and every chat) fresh hook state: without it the panel
// is re-rendered for the next visit with the previous visit's state still in place, and that
// frame is painted before the hook's reset effect runs.
export default function VisitBriefChatStrip({ schema, patient, chatPatientId, onAttached }) {
  if (!isBriefEligible(patient) || !chatPatientId) return null;
  return (
    <StripPanel
      key={`${schema || "public"}::${patient.id}::${chatPatientId}`}
      schema={schema}
      patient={patient}
      chatPatientId={chatPatientId}
      onAttached={onAttached}
    />
  );
}
